import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useSelector } from 'react-redux';
import ApolloClient from 'apollo-client';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import { MockApolloClient } from 'mock-apollo-client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useCreateEventMutation } from '../documents/CreateEventMutation.generated';
import { getCurrentUser } from '../../../../selectors/userSelectors';
import {
  ClientSummaryNode,
  CreateEventResult,
  ExpectedError,
} from '../../../../generated/webapp_gql';
import { BannerState, InfoBanner } from '../../../Errors/InfoBanner';
import CancelButton from './CancelButton/CancelButton';
import ClientSelect from '../ClientSelect/ClientSelect';
import LoadingProgress from './LoadingProgress/LoadingProgress';
import ScheduleButton from './ScheduleButton/ScheduleButton';
import ScheduledDurationDisplay from './ScheduledDurationDisplay/ScheduledDurationDisplay';
import SessionDatePicker from './SessionDatePicker/SessionDatePicker';
import SessionTimePicker from './SessionTimePicker/SessionTimePicker';
import WorkingHoursSwitch from './WorkingHoursSwitch/WorkingHoursSwitch';
import {
  canScheduleClient,
  convertStrToDateWithLocalTimezone,
  getDisplayErrorMessage,
  getSessionDurationInMinutes,
  isEligibleForHpiSessions,
} from './helpers';
import {
  ProviderAvailabilityQuery,
  SessionDetails,
  NO_SESSIONS_ERROR_MSG,
  NOT_HPI_ELIGIBLE_ERROR_MSG,
  PROVIDER_AVAILABILITY_QUERY,
} from './constants';
import { ClientSelectDocument } from '../documents/ClientSelectQuery.generated';
import { InsuranceStatus } from '../../../../constants';

export interface Props {
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onCancel: () => void;
  onShowExtendTherapyCoverageModal: (member: ClientSummaryNode) => void;
  onSuccess: (sessionDetails: SessionDetails) => void;
}

// typescript guards b/c the formattedResponse for the useCreateEventMutation can come back in the format as either a
// CreateEventResult or an ExpectedError
const isCreateEventResult = (
  result: CreateEventResult | ExpectedError,
): result is CreateEventResult => {
  return (result as CreateEventResult).uuid !== undefined;
};
const isExpectedError = (result: CreateEventResult | ExpectedError): result is ExpectedError => {
  return (result as ExpectedError).message !== undefined;
};

const ScheduleSessionForm: React.FC<Props> = ({
  memberClient,
  onCancel,
  onShowExtendTherapyCoverageModal,
  onSuccess,
}) => {
  const currentUser = useSelector(getCurrentUser);
  const defaultSessionDuration = currentUser.isTherapist ? 60 : 30;

  const [selectedClient, setSelectedClient] = useState<ClientSummaryNode>();
  const [selectedDatetime, setSelectedDatetime] = useState<Date>();
  const [sessionDuration, setSessionDuration] = useState(defaultSessionDuration);
  const [errorMessage, setErrorMessage] = useState('');
  const [ignoreWorkingHours, setIgnoreWorkingHours] = useState(false);

  const { data, loading } = useQuery<ProviderAvailabilityQuery>(PROVIDER_AVAILABILITY_QUERY, {
    client: memberClient,
    variables: {
      providerUuid: currentUser.uuid,
      ignoreWorkingHours,
      memberUuid: selectedClient?.memberUuid,
    },
    fetchPolicy: 'network-only',
  });

  const selectableDates =
    data?.providerAvailabilityForProvider?.map((availability) =>
      convertStrToDateWithLocalTimezone(availability.start),
    ) || [];
  const selectableDatesIso = selectableDates.map((date) => date.toISOString());

  // Given a time, return if the timeslot is available on the currently selected date
  const checkTimeAvailability = useCallback(
    (pickerTime: Date): boolean => {
      if (!data?.providerAvailabilityForProvider || !selectedDatetime) {
        return false;
      }

      // Proposed time is given as a Date object but the defaults to current day. Set it to the selected date
      const proposedTime = new Date(selectedDatetime);
      proposedTime.setHours(pickerTime.getHours());
      proposedTime.setMinutes(pickerTime.getMinutes());
      proposedTime.setSeconds(0);
      proposedTime.setMilliseconds(0);

      return selectableDatesIso.includes(proposedTime.toISOString());
    },
    [data, selectableDatesIso, selectedDatetime],
  );

  useEffect(() => {
    // make sure that selected time-slot is available
    // when transitioning from `ignoreWorkingHours` mode to a regular one
    if (!loading && !ignoreWorkingHours && selectedDatetime) {
      const isTimeAvailable = checkTimeAvailability(selectedDatetime);

      if (!isTimeAvailable) {
        setSelectedDatetime(undefined);
      }
    }
  }, [checkTimeAvailability, ignoreWorkingHours, loading, selectedDatetime, setSelectedDatetime]);

  useEffect(() => {
    if (data?.providerAvailabilityForProvider?.length) {
      setSessionDuration(
        getSessionDurationInMinutes(data.providerAvailabilityForProvider[0]) ||
          defaultSessionDuration,
      );
    }
  }, [data, defaultSessionDuration, setSessionDuration]);

  const { hpiRemovePendingInsuranceSessionState, showHpiClientStatusInfo } = useFlags();

  const isFormValid = canScheduleClient({
    currentUser,
    selectedClient,
    selectedDatetime,
    showHpiClientStatusInfo,
  });

  const [
    createEventMutation,
    { data: createEventMutationData, loading: isCreateEventMutationLoading },
  ] = useCreateEventMutation({
    client: memberClient,
    refetchQueries: [{ query: ClientSelectDocument }], // async invalidate client select cache to update cached member eligibility status
  });

  let endDatetime;
  if (selectedDatetime) {
    endDatetime = new Date(selectedDatetime);
    endDatetime.setMinutes(endDatetime.getMinutes() + sessionDuration);
  }

  useEffect(() => {
    if (
      !isFormValid ||
      !selectedClient ||
      !selectedDatetime ||
      !endDatetime ||
      isCreateEventMutationLoading
    ) {
      return;
    }

    if (createEventMutationData?.createEvent) {
      const { formattedResponse, ok } = createEventMutationData.createEvent;

      if (formattedResponse) {
        if (ok && isCreateEventResult(formattedResponse)) {
          onSuccess({
            endTime: endDatetime,
            member: selectedClient,
            sessionState: formattedResponse.state,
            sessionUuid: formattedResponse.uuid,
            startTime: selectedDatetime,
            sessionDuration: sessionDuration,
          });
        } else if (!ok && isExpectedError(formattedResponse)) {
          setErrorMessage(
            getDisplayErrorMessage({
              currentUser,
              error: formattedResponse.message,
              selectedClient,
            }),
          );
        }
      }
    }
  }, [
    createEventMutationData,
    currentUser,
    endDatetime,
    isCreateEventMutationLoading,
    isFormValid,
    onSuccess,
    selectedClient,
    selectedDatetime,
    setErrorMessage,
  ]);

  const handleSubmit = async () => {
    if (
      !isFormValid ||
      !selectedClient ||
      !selectedDatetime ||
      !endDatetime ||
      isCreateEventMutationLoading
    ) {
      // The submit button should be disabled, but in case it isn't, do nothing.
      return;
    }

    const variables = {
      endTime: endDatetime.toISOString(),
      ignoreWorkingHours,
      memberUuid: selectedClient.memberUuid,
      startTime: selectedDatetime.toISOString(),
    };

    try {
      await createEventMutation({ variables });
    } catch (error: any) {
      if (error) {
        setErrorMessage(
          getDisplayErrorMessage({
            currentUser,
            error: error.message || 'Something went wrong! Please try again later.',
            selectedClient,
          }),
        );
      }
    }
  };

  const handleScheduleButtonClick = () => {
    if (!selectedClient) return;

    const { isCompanyHpiEnabled, memberInsuranceStatus, visitsRemaining } = selectedClient;

    if (
      currentUser.isTherapist &&
      showHpiClientStatusInfo &&
      hpiRemovePendingInsuranceSessionState &&
      isCompanyHpiEnabled &&
      memberInsuranceStatus === InsuranceStatus.INCOMPLETE &&
      visitsRemaining === 0
    ) {
      onShowExtendTherapyCoverageModal(selectedClient);
      return;
    }

    handleSubmit();
  };

  const handleSelectDate = (selectedDate: Date) => {
    if (!data?.providerAvailabilityForProvider || loading) {
      return;
    }

    // Find the first available time for the selected date
    for (const availability of data.providerAvailabilityForProvider) {
      const availabilityStart = convertStrToDateWithLocalTimezone(availability.start);
      const availabilityEnd = convertStrToDateWithLocalTimezone(availability.end);

      if (availabilityStart.toDateString() === selectedDate.toDateString()) {
        setSelectedDatetime(availabilityStart);
        break;
      } else if (availabilityStart <= selectedDate && selectedDate < availabilityEnd) {
        // Set the selected time to midnight if the date somewhere between the date range
        setSelectedDatetime(new Date(selectedDate.toDateString()));
        break;
      }
    }
  };

  const canShowClientSelectErrorState =
    showHpiClientStatusInfo &&
    currentUser.isTherapist &&
    selectedClient &&
    !isEligibleForHpiSessions(selectedClient);

  let clientSelectErrorMessage;
  if (canShowClientSelectErrorState) {
    if (selectedClient?.isCompanyHpiEnabled) {
      clientSelectErrorMessage = NOT_HPI_ELIGIBLE_ERROR_MSG;
    } else {
      clientSelectErrorMessage = NO_SESSIONS_ERROR_MSG;
    }
  }

  return (
    <div className="h-full">
      {errorMessage && (
        <div className="pr-2">
          <InfoBanner bannerState={BannerState.Error} message={errorMessage} />
        </div>
      )}

      <h1 className="font-black text-black text-2xl mb-4">Schedule a session</h1>

      <p className="text-gray-700 text-sm leading-normal">
        You’ll be able to book clients that have available sessions left. After you book, we’ll
        notify them about the session through an email.
      </p>

      <div className="rounded-md bg-gray-200 w-full mt-6 mb-8 py-6 relative">
        <WorkingHoursSwitch checked={ignoreWorkingHours} onChange={setIgnoreWorkingHours} />

        <div className="px-3">
          <ClientSelect
            errorMessage={clientSelectErrorMessage}
            memberClient={memberClient}
            onClientSelect={setSelectedClient}
          />

          <div className="pt-6 flex">
            <div className="flex-grow">
              <SessionDatePicker
                isDisabled={canShowClientSelectErrorState}
                onChange={handleSelectDate}
                selectedDatetime={selectedDatetime}
                selectableDates={selectableDates}
              />
            </div>

            <div className="flex-grow px-1">
              <SessionTimePicker
                filterTime={checkTimeAvailability}
                isDisabled={canShowClientSelectErrorState || !selectedDatetime}
                onChange={setSelectedDatetime}
                selectedDatetime={selectedDatetime}
                timeIntervals={15}
              />
            </div>

            <div className="flex-grow-0">
              <ScheduledDurationDisplay scheduledDurationInMinutes={sessionDuration} />
            </div>
          </div>
        </div>

        {loading && <LoadingProgress />}
      </div>

      <div className="flex justify-end items-center pb-6">
        <CancelButton onClick={onCancel} />

        <ScheduleButton
          disabled={!isFormValid}
          onClick={handleScheduleButtonClick}
          submitting={isCreateEventMutationLoading}
        />
      </div>
    </div>
  );
};

export default ScheduleSessionForm;
